import React, { useState, useEffect } from "react";
import { useSelector, useDispatch, RootStateOrAny } from "react-redux";
import {
  Box,
  VStack,
  HStack,
  Button,
  Input,
  Heading,
  Pressable,
  Text,
  Image,
  Tooltip,
  IconButton,
} from "native-base";
import { CloseIcon, SearchIcon } from "native-base";
import { CreateGraduation } from "../../assets/icons/icon-assets";
import { useWindowDimension } from "../../Utility/WindowDimensions";
import _ from "lodash";
import { Loc } from "../../App/Loc";
import * as AV from "../../AVCore/AVCore";
import AVCancel from "../../components/common/AVCancel";
import { setEntitydata, setSearch } from "../../Redux/Actions/userActions";

type AddGraduationClassPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  entity?: ExtendedEntity;
  setSchoolGradClassList?: React.Dispatch<React.SetStateAction<AV.ILink[]>>;
  setSchoolYearGradClassList?: React.Dispatch<React.SetStateAction<AV.ILink[]>>;
  setEntity?: React.Dispatch<React.SetStateAction<ExtendedEntity | undefined>>;
};

type ExtendedEntity = AV.IAVEntity & {
  childOrgs?: AV.ILink[];
  subType?: string;
  season?: string;
  roles?: AV.IRole[];
  wards?: AV.ILink[];
  staff?: AV.IStaff[];
  guardians?: AV.ILink[];
  gradClasses?: AV.ILink[];
  preferences?: AV.IPreferences;
};

const AddGraduationClassPopup: React.FC<AddGraduationClassPopupProps> = ({
  isOpen,
  onClose,
  entity,
  setEntity,
  setSchoolGradClassList,
  setSchoolYearGradClassList,
}) => {
  const dispatch = useDispatch();
  const dimensions = useWindowDimension();
  const mobile = dimensions?.width < 600;
  const currentUser = useSelector(
    (state: RootStateOrAny) => state?.user?.user[0],
  );
  const localUserSearch = useSelector(
    (state: RootStateOrAny) => state.user.userSearch,
  );
  const entityDataList = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist,
  );

  const [isAPICall, setIsAPICall] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [gradClasses, setGradClasses] = useState<AV.ILink[]>([]);
  const [searchResults, setSearchResults] = useState<AV.ILink[]>([]);
  const [selectedOrgs, setSelectedOrgs] = useState<AV.ITypeLink[]>([]);

  useEffect(() => {
    const fetchGradClasses = async () => {
      try {
        let parentEntity: ExtendedEntity | undefined;

        const foundParent = entityDataList?.find(
          (x) => x?.id === entity?.parent?.id,
        );

        parentEntity =
          foundParent ?? (await AV.AVEntity.get(entity?.parent as AV.IFK));

        let results: AV.ILink[] =
          parentEntity?.childOrgs?.filter(
            (x: AV.ILink) =>
              x?.title &&
              x?.data !== "Deleted" &&
              x?.data !== "Suspended" &&
              x.type === "PlayerOrg",
          ) ?? [];

        results = results.filter(
          (x) => !entity?.gradClasses?.some((y: AV.ILink) => y?.id === x?.id),
        );

        setGradClasses(results);
      } catch (error) {
        console.error("Error fetching grad classes:", error);
        setGradClasses([]);
      }
    };

    if (isOpen && entity?.parent) {
      fetchGradClasses();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSearchItems = async (searchValue: string) => {
    setSearchQuery(searchValue);

    if (searchValue.trim().length === 0) {
      setSearchResults([]);
      return;
    }

    try {
      let results: AV.ILink[] =
        gradClasses?.filter(
          (x: AV.ILink) =>
            x?.title &&
            x?.data !== "Deleted" &&
            x?.data !== "Suspended" &&
            x.type === "PlayerOrg" &&
            //x.subType === "GradClass" &&
            (x.title.toLowerCase().includes(searchValue.toLowerCase()) ||
              x.data?.toLowerCase().includes(searchValue.toLowerCase())),
        ) ?? [];

      results = results.filter(
        (x) => !entity?.gradClasses?.some((y: AV.ILink) => y?.id === x?.id),
      );

      results = results.filter(
        (x) => !selectedOrgs.some((y: AV.ILink) => y?.id === x?.id),
      );

      const formattedResults = _.uniqBy(results, "id") as AV.ITypeLink[];
      setSearchResults(formattedResults);
    } catch (error) {
      console.error("Error searching orgs:", error);
      setSearchResults([]);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setSelectedOrgs([]);
  };

  const handleOrgSelect = (org: AV.ILink) => {
    if (!selectedOrgs.some((o) => o.id === org.id)) {
      setSelectedOrgs([org]);
      //setSelectedOrgs([...selectedOrgs, org]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleRemoveOrg = (orgId: string) => {
    setSelectedOrgs(selectedOrgs.filter((o) => o.id !== orgId));
  };

  const handleSaveOrg = async () => {
    try {
      setIsAPICall(true);

      if (!entity || selectedOrgs.length === 0) return;

      const org: ExtendedEntity = await AV.Club.addGradClassToYear(
        AV.AVEntity.getFk(entity),
        selectedOrgs[0],
      );

      console.log(org);

      if (org && org.gradClasses && org.gradClasses.length > 0) {
        if (org?.type === "SchoolYear")
          setSchoolYearGradClassList?.(org?.gradClasses || []);

        const updatedList = [...entityDataList];
        const index = updatedList.findIndex((x) => x?.id === org?.id);
        if (index !== -1) updatedList[index] = org;

        setEntity?.(org);
        dispatch(setEntitydata(updatedList));
      }

      clearSearch();
      onClose();
    } catch (error) {
      console.error("Error saving graduation class:", error);
    } finally {
      setIsAPICall(false);
    }
  };

  return (
    <Box
      position="fixed"
      bgColor="AVColor.overlay2"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={9999}
      justifyContent="center"
      alignItems="center"
    >
      <Box
        bg="white"
        borderRadius="12px"
        shadow={5}
        width={mobile ? dimensions.width - 30 : "700px"}
        height={mobile ? "40vh" : "50vh"}
        maxH="80vh"
        overflow="hidden"
      >
        {/* Header */}
        <HStack
          justifyContent="space-between"
          alignItems="center"
          px={5}
          py={4}
          borderBottomWidth={1}
          borderColor="AVColor.grey"
        >
          <Text fontSize="20px" fontWeight="600">
            {Loc.currentLang.menu.addGraduationClass}
          </Text>

          <HStack space={3}>
            <AVCancel
              color={undefined}
              size={undefined}
              callback={() => onClose()}
            />
          </HStack>
        </HStack>

        {/* Body */}
        <VStack flex={1} space={3} position="relative">
          {/* Search Input */}
          {/*<HStack alignItems="center">*/}
          {/*  <Input*/}
          {/*    flex={1}*/}
          {/*    height="42px"*/}
          {/*    borderWidth={0}*/}
          {/*    backgroundColor="#F5F5F5"*/}
          {/*    borderRadius="22px"*/}
          {/*    fontSize="16px"*/}
          {/*    variant="AVSearchInput"*/}
          {/*    placeholder={Loc.currentLang.myPlan.search}*/}
          {/*    placeholderTextColor="gray.500"*/}
          {/*    value={searchQuery}*/}
          {/*    onChangeText={handleSearchItems}*/}
          {/*    InputLeftElement={<SearchIcon ml="3" size="5" color="#000" />}*/}
          {/*    InputRightElement={*/}
          {/*      searchQuery ? (*/}
          {/*        <IconButton*/}
          {/*          icon={<CloseIcon size="xs" />}*/}
          {/*          size="sm"*/}
          {/*          variant="AVSearchInput"*/}
          {/*          onPress={() => clearSearch()}*/}
          {/*          _hover={{ bg: "transparent" }}*/}
          {/*        />*/}
          {/*      ) : undefined*/}
          {/*    }*/}
          {/*  />*/}
          {/*</HStack>*/}

          {/* Selected Orgs */}
          <Box mt={4}>
            <HStack flexWrap="wrap" space={2}>
              {selectedOrgs.map((org) => (
                <Box
                  key={org.id}
                  bg="gray.100"
                  px={3}
                  py={1}
                  mb={2}
                  borderRadius="full"
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                >
                  <HStack space={3} alignItems="center">
                    <CreateGraduation
                      height={20}
                      width={20}
                      color={"#32AE78"}
                    />

                    <VStack>
                      <Text fontWeight="medium">{org.title}</Text>
                      {org.data && (
                        <Text fontSize="xs" color="gray.500">
                          {org.data}
                        </Text>
                      )}
                    </VStack>
                    <Pressable onPress={() => handleRemoveOrg(org.id!)}>
                      <CloseIcon size="12px" color="gray.500" />
                    </Pressable>
                  </HStack>
                </Box>
              ))}
            </HStack>
          </Box>

          {/* Suggestions Dropdown */}
          {searchResults.length > 0 && (
            <Box
              position="absolute"
              top="50px"
              left={0}
              right={0}
              bg="white"
              borderRadius="md"
              shadow={4}
              zIndex={2000}
              maxH="200px"
              overflowY="auto"
            >
              {searchResults.map((org) => (
                <Pressable
                  key={org.id}
                  onPress={() => handleOrgSelect(org)}
                  p={3}
                  _hover={{ bg: "gray.50" }}
                >
                  <HStack space={3} alignItems="center">
                    <CreateGraduation
                      height={20}
                      width={20}
                      color={"#32AE78"}
                    />

                    <VStack>
                      <Text fontWeight="medium">{org.title}</Text>
                      {org.data && (
                        <Text fontSize="xs" color="gray.500">
                          {org.data}
                        </Text>
                      )}
                    </VStack>
                  </HStack>
                </Pressable>
              ))}
            </Box>
          )}

          {/* Grad Classes List */}
          {gradClasses.length > 0 && (
            <HStack flexWrap="wrap" alignSelf="flex-start" space={3} p={2}>
              {gradClasses.map((item) => {
                const isSelected = selectedOrgs.some((o) => o.id === item.id);

                return (
                  <Pressable
                    key={item.id}
                    onPress={() => {
                      if (isSelected) {
                        handleRemoveOrg(item.id!);
                      } else {
                        handleOrgSelect(item);
                      }
                    }}
                  >
                    <Box
                      key={item.id}
                      bgColor="#fff"
                      borderWidth="1px"
                      borderColor="AVColor.hovercolor2"
                      minWidth={130}
                      minHeight={150}
                      borderRadius={5}
                      m={2}
                      shadow={1}
                    >
                      <VStack alignItems="center" height="100%" width="100%">
                        {/* Thumb or default icon */}
                        {item?.thumb ? (
                          <Box
                            bgColor="#FF6600"
                            width="70px"
                            height="70px"
                            alignSelf="center"
                            p={0}
                          >
                            <Image
                              source={{ uri: AV.Media.getThumb(item) }}
                              width="100%"
                              height="100%"
                            />
                          </Box>
                        ) : (
                          <CreateGraduation
                            height={70}
                            width={70}
                            color={"#32AE78"}
                          />
                        )}

                        {/* Tooltip */}
                        {/*<Tooltip label={"HI"} openDelay={500} w={ 200 }>*/}
                        <Text
                          mt={2}
                          width="120px"
                          textAlign="center"
                          flexShrink={1}
                        >
                          {item.title}
                        </Text>
                        {/*</Tooltip>*/}
                      </VStack>
                    </Box>
                  </Pressable>
                );
              })}
            </HStack>
          )}
        </VStack>

        {/* Footer */}
        <HStack
          justifyContent="flex-end"
          px={5}
          py={4}
          space={3}
          borderColor="AVColor.grey"
        >
          <Button
            rounded="full"
            width="120px"
            height="40px"
            backgroundColor="gray.300"
            onPress={() => {
              clearSearch();
              onClose();
            }}
          >
            <Text fontSize="16px" color="gray.600">
              {Loc.currentLang.myPlan.cancel}
            </Text>
          </Button>

          <Button
            rounded="full"
            width="140px"
            height="40px"
            backgroundColor="AVColor.primary"
            isLoading={isAPICall}
            isDisabled={isAPICall || selectedOrgs.length === 0}
            onPress={handleSaveOrg}
          >
            <Text fontSize="16px" color="white">
              {Loc.currentLang.menu.add}
            </Text>
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default AddGraduationClassPopup;
